import { getPointsEarned, getSetPointWinners, ScoringSetGameState } from 'shared';
import { ScoreTable, ScoreTableRow } from './score-table';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { token } from '../../ui/theme';

interface Props {
  open: boolean;
  onClose: () => void;
  gameState: ScoringSetGameState;
}

export const ScoreResultsModal = ({ open, onClose, gameState }: Props) => {
  const { setResults, teams, scoreToWin, bidForSet, biddingPlayer } = gameState;
  // Same helpers the server scores with, so this modal can never contradict the
  // score that was actually applied.
  const winners = getSetPointWinners(setResults);

  const biddingTeamIndex = teams.findIndex((t) => t.players.includes(biddingPlayer));
  const biddingTeam = teams[biddingTeamIndex];
  const biddingTeamLabel = biddingTeamIndex >= 0 ? `Team ${biddingTeamIndex + 1}` : '?';
  const biddingTeamPoints = biddingTeam ? getPointsEarned(biddingTeam.id, setResults) : 0;
  const madeBid = biddingTeamPoints >= bidForSet;

  const columns = teams.map((_, i) => `Team ${i + 1}`);

  const rows: ScoreTableRow[] = [
    {
      type: 'point',
      label: 'High',
      cells: teams.map((t) => {
        const card = setResults[t.id]?.highTrumpCard;
        return { won: winners.high === t.id, label: card?.name };
      }),
    },
    {
      type: 'point',
      label: 'Low',
      cells: teams.map((t) => {
        const card = setResults[t.id]?.lowTrumpCard;
        return { won: winners.low === t.id, label: card?.name };
      }),
    },
    {
      type: 'point',
      label: 'Jack',
      cells: teams.map((t) => {
        const card = setResults[t.id]?.jackTrumpCard;
        return { won: winners.jack === t.id, noWinner: !winners.jack, label: card?.name };
      }),
    },
    {
      type: 'point',
      label: 'Game',
      cells: teams.map((t) => {
        const score = setResults[t.id]?.gameScore ?? 0;
        return { won: winners.game === t.id, noWinner: !winners.game, label: `${score} pts` };
      }),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={<span style={{ letterSpacing: 2 }}>Set Results</span>}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Continue
        </Button>
      }
    >
      <>
        {/* Bid banner */}
        <div
          style={{
            background: token('color-surface'),
            border: `1px solid ${token('color-border')}`,
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 20,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <span
              style={{
                color: token('color-muted'),
                fontSize: token('font-size-xs'),
                letterSpacing: 1,
              }}
            >
              BID{' '}
            </span>
            <span style={{ color: token('color-accent'), fontWeight: 700 }}>
              {biddingTeamLabel}
            </span>
            <span style={{ color: token('color-muted') }}> — </span>
            <span
              style={{
                color: token('color-gold'),
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              {bidForSet}
            </span>
          </div>
          <div
            style={{
              color: madeBid ? token('color-win') : token('color-lose'),
              fontWeight: 700,
              fontSize: token('font-size-sm'),
            }}
          >
            {madeBid ? '✓ Made it' : '✗ Set'}
          </div>
        </div>

        <ScoreTable columns={columns} rows={rows} />

        {/* Score strip */}
        <div style={{ display: 'flex', gap: 10, marginTop: 'var(--space-4)' }}>
          {teams.map((team, i) => {
            const isBidding = team.id === biddingTeam?.id;
            const pts = isBidding ? Math.min(biddingTeamPoints, bidForSet) : null;
            const isLeading = team.score === Math.max(...teams.map((x) => x.score));
            return (
              <div
                key={team.id}
                style={{
                  flex: 1,
                  background: token('color-surface'),
                  border: `1px solid ${token('color-border')}`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    color: token('color-muted'),
                    fontSize: token('font-size-xs'),
                    letterSpacing: 1,
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  TEAM {i + 1}
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'baseline',
                  }}
                >
                  {pts !== null && (
                    <span
                      style={{
                        position: 'absolute',
                        left: 0,
                        color: token('color-gold'),
                        fontWeight: 600,
                        fontSize: token('font-size-sm'),
                      }}
                    >
                      +{pts}
                    </span>
                  )}
                  <span
                    style={{
                      color: isLeading ? token('color-text') : token('color-muted'),
                      fontWeight: 700,
                      fontSize: token('font-size-lg'),
                    }}
                  >
                    {team.score}
                  </span>
                  <span
                    style={{
                      color: token('color-muted'),
                      fontSize: token('font-size-xs'),
                      marginLeft: 3,
                    }}
                  >
                    / {scoreToWin}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </>
    </Modal>
  );
};
